//React
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebase';

//Redux
import { useDispatch, useSelector } from 'react-redux';
import { changeUserType, logOut, signInGoogleAuthProvider } from '../../redux/auth/userSlice';
import { resetCalendarSchedule } from '../../redux/calendar/calendarScheduleSlice';
import { resetCalendarSettings } from '../../redux/calendar/calendarSettingsSlice';
import { fetchContacts, resetContact } from '../../redux/chat/contactSlice';
import { fetchUserMessages, findUnreadSenders, resetMessage } from '../../redux/chat/messageSlice';
import { fetchAllJobs, fetchJobsByUserId, resetJobs } from '../../redux/jobs/jobSlice';
import { fetchBookingsByQuery, resetOrder } from '../../redux/orders/orderSlice';
import {
  updateNotifications,
  resetNotifications,
  subscribeToNotifications
} from '../../redux/notifications/notificationSlice';
import { getUserProfile, resetProfileForm } from '../../redux/profiles/profileSlice';
import { resetFileUpload } from '../../redux/storage/fileUploadSlice';
import { markMessagesAsRead } from '../../redux/chat/messageSlice';
import { persistor } from '../../redux/store';

// Material UI
import { InputAdornment, TextField } from '@mui/material';
import { Search } from '@mui/icons-material';

// Components
import ContactItem from '../elements/ContactItem';
import LanguageSwitcher from '../elements/LanguageSwitcher';
import MobileMenu from '../elements/MobileMenu';
import UserDropdown from '../elements/UserDropdown';
import ProfileCompletionPrompt from '../elements/ProfileCompletionPrompt';
import { useNotifications } from '../useNotifications';
import { randomImages } from '../utils/images';
import useContactsListener from '../../hooks/useContactsListener';
import { PROFILE_REMINDER_STORAGE_KEY } from '../../utils/profileCompletion';

// Translation
import { useTranslation } from 'react-i18next';

// Pages
import LoginModal from '../../pages/LoginModal';
import { useLogin } from '../../pages/useLogin';

// Assets
import defaultProfileImg from '../../assets/profileimage.jpg';
//Styles
import '../../styles/Header.scss';

function isMouseMoreThan30pxAway(selector, mouseX, mouseY) {
  const element = document.querySelector(selector);
  if (!element) return false;

  const rect = element.getBoundingClientRect();
  const elementCenter = {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2
  };

  const dx = mouseX - elementCenter.x;
  const dy = mouseY - elementCenter.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  return distance > 30;
}

function formatNotifTime(ts) {
  if (!ts) return '';
  const diff = Date.now() - ts;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'Just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.floor(min / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return `${d}d ago`;
}

const Notifications = ({ notifications, unreadNotifications, clearAllNotifications, deleteNotification, markAsRead, markAllAsRead, notificationClick, t }) => {
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [avatarMap, setAvatarMap] = useState({});
  const notificationBoxRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (notificationBoxRef.current && !notificationBoxRef.current.contains(e.target)) {
        setNotificationOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!notifications?.length) return;
    const missingUids = [...new Set(
      notifications.map(n => n.senderUid).filter(uid => uid && !avatarMap[uid])
    )];
    if (!missingUids.length) return;
    Promise.all(
      missingUids.map(uid =>
        getDoc(doc(db, 'users', uid, 'data', 'userdata'))
          .then(snap => ({ uid, photoURL: snap.data()?.photoURL || '' }))
          .catch(() => ({ uid, photoURL: '' }))
      )
    ).then(results => {
      const newMap = {};
      results.forEach(r => { newMap[r.uid] = r.photoURL; });
      setAvatarMap(prev => ({ ...prev, ...newMap }));
    });
  }, [notifications]);

  const notificationDropdown = (
    <div className="notif-dropdown" onClick={(e) => e.stopPropagation()}>
            <div className="notif-header">
              <div className="notif-header-left">
                <span className="notif-title">{t('header.notifications')}</span>
                {unreadNotifications.length > 0 && (
                  <span className="notif-count-pill">{unreadNotifications.length}</span>
                )}
              </div>
              <div className="notif-header-right">
                {unreadNotifications.length > 0 && (
                  <button className="notif-action-btn" onClick={markAllAsRead}>
                    Mark all read
                  </button>
                )}
                <button className="notif-action-btn notif-action-btn--clear" onClick={clearAllNotifications}>
                  {t('header.Clear All')}
                </button>
              </div>
            </div>

            <div className="notif-list">
              {notifications && notifications.length ? (
                [...notifications].map((n, i) => ({ ...n, _origIndex: i })).reverse().map((notification) => (
                  <div
                    key={notification._origIndex}
                    className={`notif-item${!notification.read ? ' notif-item--unread' : ''}`}
                    onClick={() => {
                      markAsRead(notification._origIndex);
                      notificationClick(notification);
                      setNotificationOpen(false);
                    }}>
                    <div className="notif-avatar-wrapper">
                      <img
                        className="notif-avatar"
                        src={avatarMap[notification.senderUid] || defaultProfileImg}
                        onError={(e) => { e.target.onerror = null; e.target.src = defaultProfileImg; }}
                        referrerPolicy="no-referrer"
                        alt=""
                      />
                      <span
                        className="material-icons-outlined notif-icon-badge"
                        style={{ color: notification.color }}>
                        {notification.icon}
                      </span>
                    </div>
                    <div className="notif-content">
                      <p className="notif-text">
                        <span className="notif-sender">{notification.name}</span>
                        {' '}{notification.text}
                      </p>
                      {notification.createdAt && (
                        <span className="notif-time">{formatNotifTime(notification.createdAt)}</span>
                      )}
                    </div>
                    <div className="notif-meta">
                      {!notification.read && <span className="notif-unread-dot" />}
                      <button
                        className="material-icons-outlined notif-delete-btn"
                        onClick={(e) => { e.stopPropagation(); deleteNotification(notification._origIndex); }}>
                        close
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="notif-empty">
                  <span className="material-icons-outlined notif-empty-icon">notifications_none</span>
                  <p>{t('header.no-notifications')}</p>
                </div>
              )}
            </div>
          </div>
  );

  return (
    <>
      <li
        ref={notificationBoxRef}
        onClick={() => setNotificationOpen((prev) => !prev)}
        className={`nav-notificationBox${notificationOpen ? ' nav-notificationBox--open' : ''}`}>
        <div className="notif-bell-wrapper">
          <span className="material-icons-outlined notif-bell-icon">notifications</span>
          {unreadNotifications.length > 0 && (
            <span className="notif-badge">{unreadNotifications.length}</span>
          )}
        </div>
        <div className="dropdown notification-dropdown-desktop">
          {notificationOpen && notificationDropdown}
        </div>
      </li>
      {notificationOpen && (
        <li
          className="notification-dropdown-mobile-panel"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}>
          {notificationDropdown}
        </li>
      )}
    </>
  );
};

const RECENT_CONTACTS_LIMIT = 5;
const EMPTY_ARRAY = [];

const MessagesDropdown = ({ t, userId, userType }) => {
  const [messagesOpen, setMessagesOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('recent');
  const messagesBoxRef = useRef(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const unreadContacts = useSelector((state) => state.message.unreadContacts);
  const contacts = useSelector((state) => state.chat.contacts);
  const userMessages = useSelector((state) => Array.isArray(state.message.userMessages) ? state.message.userMessages : EMPTY_ARRAY);

  const allContacts = useMemo(() => {
    const unreadMap = Object.fromEntries(unreadContacts.map((uc) => [uc.chatId, uc.unreadCount]));
    //const msgMap = Object.fromEntries(userMessages.map((um) => [um.chatId, um.messages[0] ?? null]));
    const msgMap = Object.fromEntries((userMessages ?? []).map((um) => [um.chatId, um.messages?.[0] ?? null])); //prevent undefined errors

    return contacts
      .map((c) => ({
        chatId: c.chatId,
        contactId: c.contactUid,
        contactName: c.name,
        contactPhotoURL: c.photoURL,
        lastMessage: msgMap[c.chatId] ?? null,
        unreadCount: unreadMap[c.chatId] ?? 0
      }))
      .sort((a, b) => {
        const ta = a.lastMessage?.timestamp?.seconds ?? 0;
        const tb = b.lastMessage?.timestamp?.seconds ?? 0;
        return tb - ta;
      });
  }, [contacts, userMessages, unreadContacts]);

  const unreadList = useMemo(
    () =>
      allContacts
        .filter((c) => c.unreadCount > 0)
        .filter((c) => c.contactName.toLowerCase().includes(searchTerm.toLowerCase())),
    [allContacts, searchTerm]
  );

  const recentList = useMemo(
    () =>
      allContacts
        .filter((c) => c.contactName.toLowerCase().includes(searchTerm.toLowerCase()))
        .slice(0, RECENT_CONTACTS_LIMIT),
    [allContacts, searchTerm]
  );

  const handleMarkAllRead = async () => {
    if (unreadContacts.length === 0) return;
    await Promise.all(
      unreadContacts.map((contact) =>
        dispatch(markMessagesAsRead({ chatId: contact.chatId, currentUserId: userId }))
      )
    );
    dispatch(findUnreadSenders(userId));
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (messagesBoxRef.current && !messagesBoxRef.current.contains(e.target)) {
        setMessagesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const messagesDropdown = (
    <div
      className="dropdown-content-messages"
      onClick={(e) => e.stopPropagation()}>
      <div className="search-input-container">
        <TextField
          fullWidth
          size="small"
          type="text"
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: '10px',
              fontSize: '14px',
            },
            '& fieldset': {
              borderColor: '#B5C5C2',
            },
          }}
          placeholder={t('header.Search')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            )
          }}
        />
      </div>
      <div className="msg-tab-bar">
        <button
          className={`msg-tab${activeTab === 'recent' ? ' msg-tab--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); setActiveTab('recent'); }}>
          {t('header.Recent')}
        </button>
        <button
          className={`msg-tab${activeTab === 'unread' ? ' msg-tab--active' : ''}`}
          onClick={(e) => { e.stopPropagation(); setActiveTab('unread'); }}>
          {t('header.Unread')}
          {unreadList.length > 0 && <span className="msg-tab-count">{unreadList.length}</span>}
        </button>
        {activeTab === 'unread' && unreadContacts.length > 0 && (
          <p onClick={handleMarkAllRead} className="delete-all">
            {t('header.Mark all read')}
          </p>
        )}
      </div>
      <div className="notification-message-box">
        {activeTab === 'unread' ? (
          unreadList.length === 0 ? (
            <div className="notification-message-box-empty">{t('header.no-messages')}</div>
          ) : (
            unreadList.map((contact, i) => (
              <ContactItem
                key={`u-${i}`}
                contactName={contact.contactName}
                contactPhotoURL={contact.contactPhotoURL}
                unreadCount={contact.unreadCount}
                lastMessage={contact.lastMessage}
                currentUserId={userId}
                urlLocation={`/${userType}/messages`}
                contactData={contact}
                onSelect={() => setMessagesOpen(false)}
              />
            ))
          )
        ) : (
          recentList.length === 0 ? (
            <div className="notification-message-box-empty">{t('header.no-messages')}</div>
          ) : (
            recentList.map((contact, i) => (
              <ContactItem
                key={`r-${i}`}
                contactName={contact.contactName}
                contactPhotoURL={contact.contactPhotoURL}
                unreadCount={contact.unreadCount}
                lastMessage={contact.lastMessage}
                currentUserId={userId}
                urlLocation={`/${userType}/messages`}
                contactData={contact}
                onSelect={() => setMessagesOpen(false)}
              />
            ))
          )
        )}
      </div>
      <div className="msg-view-all" onClick={() => navigate(`/${userType}/messages`)}>
        {t('header.View all messages')}
      </div>
    </div>
  );

  return (
    <>
      <li
        ref={messagesBoxRef}
        onClick={() => setMessagesOpen((prev) => !prev)}
        className={`nav-messageBox${messagesOpen ? ' nav-messageBox--open' : ''}`}>
        <div className="icon-and-red-circle">
          <span className="material-icons-outlined">forum</span>
          {unreadContacts.length > 0 && (
            <span className="red-circle">{unreadContacts.length}</span>
          )}
        </div>
        <div className="dropdown message-dropdown-desktop">
          {messagesOpen && messagesDropdown}
        </div>
      </li>
      {messagesOpen && (
        <li
          className="message-dropdown-mobile-panel"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}>
          {messagesDropdown}
        </li>
      )}
    </>
  );
};

const MainNavigation = ({ menuOpen }) => {
  const { t } = useTranslation();
  useContactsListener();
  const _user = useSelector((state) => state.user);
  const _profile = useSelector((state) => state.profile.userProfile);
  const _notifications = useSelector((state) => state.notification.notifications);
  const [profileImage, setProfileImage] = useState(defaultProfileImg);
  const [showSellerProfileReminder, setShowSellerProfileReminder] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (_user?.uid) {
      dispatch(getUserProfile(_user.uid));
      dispatch(fetchContacts(_user.uid));
    }
  }, [_user?.uid]);

  useEffect(() => {
    if (!_user?.uid) return;
    const unsubscribe = subscribeToNotifications(_user.uid, dispatch);
    return () => unsubscribe();
  }, [_user?.uid, dispatch]);

  useEffect(() => {
    const googleUrl = _user?.photoURL?.startsWith('http') ? _user.photoURL : null;
    const customUrl = _profile?.url?.startsWith('http') ? _profile.url : null;
    setProfileImage(googleUrl || customUrl || defaultProfileImg);
  }, [_user, _profile]);

  useEffect(() => {
    if (!_user?.uid) return;
    dispatch(findUnreadSenders(_user.uid));
    dispatch(fetchUserMessages(_user.uid));
    dispatch(fetchJobsByUserId(_user.uid));
    dispatch(fetchBookingsByQuery(_user.uid));
  }, [dispatch, _user?.uid]);

  const { notifications, unreadNotifications } = useNotifications(_notifications, dispatch, _user);
  const {
    showLoginModal,
    setShowLoginModal,
    username,
    setUsername,
    usermail,
    setUsermail,
    password,
    setPassword,
    remember,
    setRemember,
    randNumber,
    setRandNumber,
    handleSubmit,
    loginError,
    isLoading,
    setIsLoading,
    isEmailNotVerified,
    handleResendVerification,
  } = useLogin(dispatch);

  const onLogoutClick = () => {
    dispatch(logOut());
    dispatch(resetCalendarSchedule());
    dispatch(resetCalendarSettings());
    dispatch(resetFileUpload());
    dispatch(resetJobs());
    dispatch(resetProfileForm());
    dispatch(resetOrder());
    dispatch(resetContact());
    dispatch(resetMessage());
    dispatch(resetNotifications());
    persistor.purge();
    navigate('/');
  };

  const onGoogleClick = async () => {
    try {
      const result = await dispatch(signInGoogleAuthProvider({ remember })).unwrap();
      const uid = result.uid;
      dispatch(getUserProfile(uid));
      dispatch(fetchAllJobs());
      setShowLoginModal(false);
      navigate('/');  // 
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  // Function handling deletion of all notification
  function clearAllNotifications() {
    dispatch(
      updateNotifications({
        uid: _user.uid,
        notifications: []
      })
    );
  }

  // Function handling deletion of a single notification
  const deleteNotification = (NotificationIndex) => {
    let new_notifications = [...notifications];
    new_notifications.splice(NotificationIndex, 1);
    dispatch(
      updateNotifications({
        uid: _user.uid,
        notifications: new_notifications
      })
    );
  };

  const markAsRead = (index) => {
    if (notifications[index]?.read) return;
    const updated = notifications.map((n, i) => i === index ? { ...n, read: true } : n);
    dispatch(updateNotifications({ uid: _user.uid, notifications: updated }));
  };

  const markAllAsRead = () => {
    const updated = notifications.map(n => ({ ...n, read: true }));
    dispatch(updateNotifications({ uid: _user.uid, notifications: updated }));
  };

  const getNotificationTargetRole = (notification) => {
    if (notification?.targetRole) return notification.targetRole;
    if (notification?.to?.startsWith('/buyer/')) return 'buyer';
    if (notification?.to?.startsWith('/seller/')) return 'seller';
    return null;
  };

  const getNotificationTargetPath = (notification) => {
    if (notification?.targetPath) return notification.targetPath;
    if (notification?.to) return notification.to;

    const query = notification?.targetId ? `?orderId=${notification.targetId}` : '';
    if (notification?.targetRole && notification?.targetPage) {
      return `/${notification.targetRole}/${notification.targetPage}${query}`;
    }

    return '/';
  };

  // Keeps notification navigation in the same buyer/seller context as the target item.
  const notificationClick = async (notification) => {
    const targetRole = getNotificationTargetRole(notification);
    const targetPath = getNotificationTargetPath(notification);

    if (_user?.uid && targetRole && _user.userType !== targetRole) {
      try {
        await dispatch(changeUserType({ uid: _user.uid, userType: targetRole })).unwrap();
      } catch (error) {
        console.error('Unable to switch role from notification:', error);
      }
    }

    navigate(targetPath, { state: { notificationOpenedAt: Date.now() } });
  };

  const showSellerReminder = () => {
    setShowSellerProfileReminder(true);
  };




  // ------------------------------ Users ------------------------------------------
  const User = () => {
    const [profileOpen, setProfileOpen] = useState(false);
    const userBoxRef = useRef(null);

    useEffect(() => {
      const handleClickOutside = (e) => {
        if (userBoxRef.current && !userBoxRef.current.contains(e.target)) {
          setProfileOpen(false);
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
      <li
        ref={userBoxRef}
        onClick={() => setProfileOpen((prev) => !prev)}
        className="nav-userBox"
      >
        <img
          src={profileImage}
          onError={(e) => {
            e.target.onerror = null;
            e.target.src = defaultProfileImg;
          }}
          referrerPolicy="no-referrer"
          alt="Profile"
          className="profile-img"
        />
        <div className="dropdown">
          {profileOpen && (
            <div onClick={(e) => e.stopPropagation()}>
              <UserDropdown logoutClick={onLogoutClick} onSellerProfileReminder={showSellerReminder} />
            </div>
          )}
        </div>
      </li>
    );
  };

  return (
    <div className={menuOpen ? 'topNav' : 'burger_sideMenu'}>
      <nav className="mainMenu">
        <ul className="nav-serviceTitles">
          <li>
            <NavLink to="/how-service-works">
              <div className="content">{t('header.services')} </div>
            </NavLink>
          </li>
          <li>
            <NavLink to="/business-solutions">
              <div className="content">{t('header.business solutions')} </div>
            </NavLink>
          </li>
        </ul>
        {_user.uid ? (
          <ul className="nav-afterRegisterTitles">
            <MessagesDropdown userId={_user.uid} userType={_user.userType} t={t} />
            <Notifications
              notifications={notifications}
              unreadNotifications={unreadNotifications}
              clearAllNotifications={clearAllNotifications}
              deleteNotification={deleteNotification}
              markAsRead={markAsRead}
              markAllAsRead={markAllAsRead}
              notificationClick={notificationClick}
              t={t}
            />
            <li className="mobile-lang-switch">
              <LanguageSwitcher />
            </li>
            <User />
          </ul>
        ) : (
          <ul className="nav-beforeRegisterTitles">
            <li
              onClick={() => {
                setRandNumber(Math.floor(Math.random() * randomImages.length));
                setShowLoginModal(true);
              }}>
              <p className="sign-in-label login">{t('header.sign in')}</p>
            </li>
            <li>
              <NavLink to="/user-registration">
                <p className="sign-in-label register">{t('header.register')}</p>
              </NavLink>
            </li>
            <li className="guest-lang-switch">
              <LanguageSwitcher />
            </li>
          </ul>
        )}
        {_user.uid ? (
          <ul className="mobile_menu">
            <li>
              <MobileMenu logoutClick={onLogoutClick} onSellerProfileReminder={showSellerReminder} />
            </li>
          </ul>
        ) : null}
        {_user.uid ? (
          <ul>
            <li className="lang-switch">
              <LanguageSwitcher />
            </li>
          </ul>
        ) : null}

      </nav>
      <ProfileCompletionPrompt
        open={showSellerProfileReminder}
        variant="reminder"
        onClose={() => setShowSellerProfileReminder(false)}
        onDontShowAgain={() => localStorage.setItem(PROFILE_REMINDER_STORAGE_KEY, 'true')}
      />
      {showLoginModal && (
        <LoginModal
          setShowLoginModal={setShowLoginModal}
          randomImages={randomImages}
          randNumber={randNumber}
          username={username}
          setUsername={setUsername}
          usermail={usermail}
          setUsermail={setUsermail}
          password={password}
          setPassword={setPassword}
          handleSubmit={handleSubmit}
          onGoogleClick={onGoogleClick}
          remember={remember}
          setRemember={setRemember}
          loginError={loginError}
          isLoading={isLoading}
          isEmailNotVerified={isEmailNotVerified}
          handleResendVerification={handleResendVerification}
        />
      )}
    </div>
  );
};

export default memo(MainNavigation);
